import { useState, useEffect } from 'react';
import axios from 'axios';
import { PiggyBank, Building, Plus, CreditCard, Landmark, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    entity_id: '',
    account_name: '',
    bank_name: '',
    account_number: '',
    transit_number: '',
    institution_number: '',
    account_type: 'checking',
    current_balance: 0,
    is_primary: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accRes, propsRes] = await Promise.all([
        axios.get(`${API}/bank-accounts`),
        axios.get(`${API}/corporations`)
      ]);
      setAccounts(accRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/bank-accounts`, {
        ...formData,
        current_balance: parseFloat(formData.current_balance) || 0
      });
      setShowModal(false);
      setFormData({
        entity_id: '', account_name: '', bank_name: '', account_number: '',
        transit_number: '', institution_number: '', account_type: 'checking',
        current_balance: 0, is_primary: false
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create account:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const totalBalance = accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const operatingBalance = accounts.filter(a => a.account_type === 'checking').reduce((sum, a) => sum + (a.current_balance || 0), 0);
  const reserveBalance = accounts.filter(a => a.account_type === 'savings').reduce((sum, a) => sum + (a.current_balance || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-accounts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Bank Accounts</h1>
          <p className="text-slate-500">Manage property bank accounts for reconciliation</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg hover:from-indigo-700 hover:to-violet-700 transition-colors"
          data-testid="add-account-btn"
        >
          <Plus className="w-4 h-4" />
          Add Account
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl p-5 text-white">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <PiggyBank className="w-6 h-6" />
            </div>
            <div>
              <p className="text-indigo-100 text-sm">Total Balance</p>
              <p className="text-2xl font-bold">{formatCurrency(totalBalance)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <CreditCard className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-slate-500 text-sm">Operating Accounts</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(operatingBalance)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-2 rounded-lg">
              <Landmark className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-slate-500 text-sm">Reserve Funds</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(reserveBalance)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Accounts List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {accounts.map((account) => (
          <div key={account.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${
                  account.account_type === 'checking' ? 'bg-emerald-100' : 'bg-amber-100'
                }`}>
                  {account.account_type === 'checking' ? 
                    <CreditCard className="w-6 h-6 text-emerald-600" /> :
                    <Landmark className="w-6 h-6 text-amber-600" />
                  }
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{account.account_name}</h3>
                  <p className="text-sm text-slate-500">{account.bank_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {account.is_primary && (
                  <span className="text-xs px-2 py-1 rounded-full bg-indigo-100 text-indigo-700">Primary</span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                  account.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                }`}>
                  {account.status}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Account Number</p>
                  <p className="font-mono text-slate-900">{account.account_number}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Current Balance</p>
                  <p className="text-xl font-bold text-slate-900">{formatCurrency(account.current_balance)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
              <Building className="w-4 h-4" />
              {account.entity_name}
            </div>
          </div>
        ))}
      </div>

      {accounts.length === 0 && (
        <div className="text-center py-12">
          <PiggyBank className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No bank accounts found. Add your first account to start tracking finances.</p>
        </div>
      )}

      {/* Add Account Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Add Bank Account</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property *</label>
                <select
                  required
                  value={formData.entity_id}
                  onChange={(e) => setFormData({...formData, entity_id: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Property</option>
                  {properties.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Account Name *</label>
                <input
                  type="text"
                  required
                  value={formData.account_name}
                  onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., Operating Account, Reserve Fund"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.bank_name}
                    onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., TD Bank"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Account Type *</label>
                  <select
                    value={formData.account_type}
                    onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="checking">Checking (Operating)</option>
                    <option value="savings">Savings (Reserve Fund)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Transit #</label>
                  <input
                    type="text"
                    value={formData.transit_number}
                    onChange={(e) => setFormData({...formData, transit_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="12345"
                    maxLength={5}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Institution #</label>
                  <input
                    type="text"
                    value={formData.institution_number}
                    onChange={(e) => setFormData({...formData, institution_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="004"
                    maxLength={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Account # *</label>
                  <input
                    type="text"
                    required
                    value={formData.account_number}
                    onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="1234567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Opening Balance</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.current_balance}
                  onChange={(e) => setFormData({...formData, current_balance: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="0.00"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={formData.is_primary}
                  onChange={(e) => setFormData({...formData, is_primary: e.target.checked})}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                />
                <label htmlFor="is_primary" className="text-sm text-slate-700">Set as primary account for this property</label>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  Add Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
